<template>
    <app-layout>
        <page-header title="Roles/Permissions Matrix" title-in-app-header>
            <teleport v-if="$page.props.can_edit" defer to="#page-header-action">
                <jet-button v-if="!editMode" type="button" variant="secondary" @click="enterEditMode">Edit</jet-button>
                <div v-else class="flex items-center gap-2">
                    <jet-button type="button" variant="tertiary" @click="cancelEdit">Cancel</jet-button>
                    <jet-button :disabled="changeCount === 0" type="button" variant="secondary" @click="openConfirm"
                        >Save ({{ changeCount }})</jet-button
                    >
                </div>
            </teleport>
        </page-header>

        <div class="mb-6 rounded-lg border bg-white shadow-sm">
            <div class="p-4 text-sm">
                <strong>Legend:</strong>
                <span class="ms-4 inline-flex items-center">
                    <span class="me-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-600">
                        <icon class="!h-3 !w-3 text-white" icon="cil-check-alt" size="sm" />
                    </span>
                    granted
                </span>
                <span class="ms-4 inline-flex items-center">
                    <span class="me-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-600">
                        <icon class="!h-3 !w-3 text-white" icon="cil-x" size="sm" />
                    </span>
                    revoked
                </span>
                <span class="ms-4 inline-flex items-center">
                    <span class="me-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white">
                        <icon class="!h-3 !w-3 !text-green-600" icon="cil-check-alt" size="sm" />
                    </span>
                    granted (overridden)
                </span>
                <span class="ms-4 inline-flex items-center">
                    <span class="me-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white">
                        <icon class="!h-3 !w-3 !text-red-600" icon="cil-x" size="sm" />
                    </span>
                    revoked (overridden)
                </span>
            </div>
        </div>

        <div v-for="group in groupedPermissions" :key="group.group" class="mb-8">
            <h4 class="mb-1">{{ groupName(group) }}</h4>
            <div class="mb-6">
                <permissions-table
                    :edit-mode="editMode"
                    :pending-by-key="pending"
                    :permissions="group.permissions.data"
                    :roles="$page.props.roles"
                    @cell-toggle="onCellToggle"
                />
            </div>
        </div>

        <confirm-changes-modal ref="confirmModalRef" :changes="changeList" :processing="submitting" @confirm="submit" />
    </app-layout>
</template>

<script setup>
import { computed, ref } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { visitModal } from '@inertiaui/modal-vue';
import AppLayout from '@/Layouts/AppLayout.vue';
import JetButton from '@/Jetstream/Button.vue';
import PageHeader from '@/Components/PageHeader.vue';
import PermissionsTable from '@/Pages/Users/Permissions/PermissionsTable.vue';
import ConfirmChangesModal from '@/Pages/Users/Permissions/components/ConfirmChangesModal.vue';

const page = usePage();

const editMode = ref(false);
const pending = ref({});
const confirmModalRef = ref(null);
const submitting = ref(false);

const groupedPermissions = computed(() => {
    const grouped = [];
    page.props.permissions.data.forEach((permission) => {
        let group = grouped.find((el) => el.group === permission.group);
        if (!group) {
            group = { group: permission.group, permissions: { data: [], meta: { total: 0 } } };
            grouped.push(group);
        }
        group.permissions.data.push(permission);
        group.permissions.meta.total += 1;
    });
    return grouped;
});

const changeList = computed(() =>
    Object.values(pending.value)
        .filter((p) => p.action !== p.original_action)
        .map((p) => ({
            role: p.role,
            role_label: roleLabel(p.role),
            permission: p.permission,
            permission_description: permissionDescription(p.permission),
            action: p.action,
            kind: p.action === null ? 'reset' : 'set',
            was_default: p.original_action === null,
            previous_override: p.original_action,
        })),
);

const changeCount = computed(() => changeList.value.length);

function enterEditMode() {
    editMode.value = true;
    pending.value = {};
}

function cancelEdit() {
    editMode.value = false;
    pending.value = {};
}

function roleLabel(name) {
    return page.props.roles.find((r) => r.name === name)?.label ?? name;
}

function permissionDescription(name) {
    return page.props.permissions.data.find((p) => p.name === name)?.description ?? name;
}

function onCellToggle({ permission, role }) {
    const key = `${role}::${permission}`;
    const cell = findCell(permission, role);
    const original = cell.override;
    const current = pending.value[key]?.action ?? original;
    const next = cycleNext(current, cell.default);

    const cellPreview = {
        ...cell,
        override: next,
        effective: next === 'grant' ? true : next === 'revoke' ? false : cell.default,
    };

    pending.value = {
        ...pending.value,
        [key]: {
            role,
            permission,
            action: next,
            original_action: original,
            cell: cellPreview,
        },
    };
}

function cycleNext(current, isDefault) {
    // Tri-state cycle: default → opposite override → null (back to default)
    if (current === null) {
        return isDefault ? 'revoke' : 'grant';
    }
    return null;
}

function findCell(permission, role) {
    const row = page.props.permissions.data.find((p) => p.name === permission);
    return row[role];
}

function openConfirm() {
    visitModal('#confirm-permission-changes');
}

function submit() {
    const changes = Object.values(pending.value).filter((p) => p.action !== p.original_action);

    const overrides = changes
        .filter((p) => p.action !== null)
        .map((p) => ({
            role: p.role,
            permission: p.permission,
            action: p.action,
        }));

    const resets = changes
        .filter((p) => p.action === null && p.original_action !== null)
        .map((p) => ({
            role: p.role,
            permission: p.permission,
        }));

    submitting.value = true;
    router.put(
        route('permissions.update'),
        { overrides, resets },
        {
            preserveScroll: true,
            onSuccess: () => {
                editMode.value = false;
                pending.value = {};
                confirmModalRef.value?.close();
            },
            onFinish: () => {
                submitting.value = false;
            },
        },
    );
}

function groupName(group) {
    return group.group.replace('_', ' ').toUpperCase();
}
</script>
